import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Badge, Alert, Spinner, Form } from 'react-bootstrap';
import { getCart, removeFromCart, updateCartQuantity, buyCart } from '../../../services/partsTech';
import PartsTechStoreStock from './PartsTechStoreStock';
import { useToast } from '../../../global_components/Toast';

export default function PartsTechCartModal({ show, onHide, onOrderPlaced, addPart, markup = 1.95, markupType = 1 }) {
    const { showToast } = useToast();
    const [cart, setCart]               = useState(null);
    const [loading, setLoading]         = useState(false);
    const [placing, setPlacing]         = useState(false);
    const [error, setError]             = useState('');
    const [orderSummary, setOrderSummary] = useState(null); // set after successful order

    useEffect(() => {
        if (show) { setOrderSummary(null); fetchCart(); }
    }, [show]);

    async function fetchCart() {
        setLoading(true);
        setError('');
        try {
            const data = await getCart();
            setCart(data);
        } catch (e) {
            setError('Failed to load cart.');
        } finally {
            setLoading(false);
        }
    }

    async function handleRemove(itemId) {
        try { await removeFromCart([itemId]); await fetchCart(); }
        catch (e) { setError('Failed to remove item.'); }
    }

    async function handleQtyChange(itemId, qty) {
        if (qty < 1) return;
        try { await updateCartQuantity(itemId, qty); await fetchCart(); }
        catch (e) { setError('Failed to update quantity.'); }
    }

    // Build the snapshot of orders we're about to place, then call buyCart
    async function placeOrders(ordersToPlace) {
        setPlacing(true);
        setError('');

        // Snapshot items before the API call wipes the cart
        const snapshot = ordersToPlace.map(order => ({
            supplier:  order.supplier?.name || 'Unknown',
            store:     order.store?.name    || '',
            total:     order.builtOrder?.priceTotal || 0,
            shipping:  order.builtOrder?.selectedFrightTermService?.method || '',
            items: order.items.map(item => ({
                partNumber:  item.partNumber,
                description: item.partName || item.builtItem?.product?.title || '',
                quantity:    item.quantity,
                cost:        item.builtItem?.product?.customerPrice ?? item.builtItem?.product?.price ?? 0,
                coreCharge:  item.builtItem?.product?.coreCharge   ?? 0,
                distributor: order.supplier?.name || '',
            })),
        }));

        try {
            const payload = ordersToPlace.map(order => ({
                orderId:            order.id,
                shippingMethodCode: order.builtOrder?.selectedFrightTermService?.methodCode || 'TRK',
                items:              order.items.map(i => ({ itemId: i.id })),
            }));
            await buyCart(payload);

            // Add every ordered item to the invoice
            if (addPart) {
                for (const order of snapshot) {
                    for (const item of order.items) {
                        const cost = parseFloat(item.cost) || 0;
                        const list = markupType === 2
                            ? parseFloat((cost + parseFloat(markup)).toFixed(2))
                            : parseFloat((cost * markup).toFixed(2));
                        await addPart({
                            partNumber:   item.partNumber,
                            description:  item.description.toUpperCase(),
                            quantity:     item.quantity,
                            cost,
                            list,
                            distributor:  item.distributor,
                            markup,
                            markup_type:  markupType,
                        });
                    }
                }
            }

            // Build a concise toast message
            const totalParts = snapshot.reduce((sum, o) => sum + o.items.length, 0);
            const suppliers  = snapshot.map(o => o.supplier).join(', ');
            showToast(
                `Order placed! ${totalParts} part${totalParts !== 1 ? 's' : ''} from ${suppliers} added to the invoice.`,
                'success',
                6000
            );

            setOrderSummary(snapshot);
            setCart(null);
            if (onOrderPlaced) onOrderPlaced();
        } catch (e) {
            const msg = e?.response?.data?.errors?.[0]?.message || e?.response?.data?.error || 'Failed to place order.';
            setError(msg);
        } finally {
            setPlacing(false);
        }
    }

    function handlePlaceOrder(order) { placeOrders([order]); }
    function handlePlaceAll()        { if (cart?.orders?.length) placeOrders(cart.orders); }

    const orders     = cart?.orders || [];
    const totalItems = orders.reduce((sum, o) => sum + o.items.length, 0);
    const grandTotal = orders.reduce((sum, o) => sum + (o.builtOrder?.priceTotal || 0), 0);

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>
                    {orderSummary ? 'Order Confirmed' : 'PartsTech Cart'}
                    {!orderSummary && totalItems > 0 && (
                        <Badge variant="primary" style={{ marginLeft: 10, fontSize: '0.7em' }}>
                            {totalItems} item{totalItems !== 1 ? 's' : ''}
                        </Badge>
                    )}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* ── Success summary ── */}
                {orderSummary && (
                    <OrderConfirmation summary={orderSummary} onClose={onHide} />
                )}

                {/* ── Cart view ── */}
                {!orderSummary && (
                    <>
                        {loading && (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                                <p className="text-muted mt-2">Loading cart...</p>
                            </div>
                        )}

                        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

                        {!loading && orders.length === 0 && (
                            <p className="text-muted text-center py-4">Your PartsTech cart is empty.</p>
                        )}

                        {!loading && orders.map(order => (
                            <OrderSection
                                key={order.id}
                                order={order}
                                placing={placing}
                                onRemove={handleRemove}
                                onQtyChange={handleQtyChange}
                                onPlaceOrder={handlePlaceOrder}
                            />
                        ))}

                        {!loading && orders.length > 1 && (
                            <div style={{ marginTop: 16, padding: '12px 16px', background: '#f8f9fa', borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <strong>Grand Total: ${grandTotal.toFixed(2)}</strong>
                                <Button variant="success" disabled={placing} onClick={handlePlaceAll}>
                                    {placing ? <Spinner animation="border" size="sm" /> : `Place All Orders (${orders.length} distributors)`}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>

            {!orderSummary && (
                <Modal.Footer>
                    <Button variant="secondary" onClick={fetchCart} disabled={loading}>Refresh</Button>
                    <Button variant="secondary" onClick={onHide}>Close</Button>
                </Modal.Footer>
            )}
        </Modal>
    );
}

// ── Order confirmation screen ─────────────────────────────────────────────────
function OrderConfirmation({ summary, onClose }) {
    const totalParts = summary.reduce((sum, o) => sum + o.items.length, 0);
    const grandTotal = summary.reduce((sum, o) => sum + o.total, 0);

    return (
        <div>
            {/* Banner */}
            <div style={{ background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 6, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: '1.8em' }}>✅</span>
                <div>
                    <div style={{ fontWeight: 700, fontSize: '1.05em', color: '#155724' }}>
                        Order placed — {totalParts} part{totalParts !== 1 ? 's' : ''} added to invoice
                    </div>
                    <div style={{ color: '#155724', fontSize: '0.88em', marginTop: 2 }}>
                        All items have been added to the current invoice with your shop markup applied.
                    </div>
                </div>
            </div>

            {/* Per-distributor breakdown */}
            {summary.map((order, oi) => (
                <div key={oi} style={{ marginBottom: 16, border: '1px solid #dee2e6', borderRadius: 6, overflow: 'hidden' }}>
                    {/* Header */}
                    <div style={{ background: '#343a40', color: '#fff', padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <strong>{order.supplier}</strong>
                            {order.store && <span style={{ marginLeft: 10, fontSize: '0.85em', opacity: 0.75 }}>{order.store}</span>}
                            {order.shipping && (
                                <Badge variant="info" style={{ marginLeft: 10, fontSize: '0.72em' }}>{order.shipping}</Badge>
                            )}
                        </div>
                        <span style={{ fontSize: '0.9em' }}>Order Total: <strong>${order.total.toFixed(2)}</strong></span>
                    </div>

                    {/* Items */}
                    <Table size="sm" style={{ margin: 0 }}>
                        <thead>
                            <tr style={{ background: '#f0f4ff' }}>
                                <th>Part #</th>
                                <th>Description</th>
                                <th style={{ width: 60, textAlign: 'center' }}>Qty</th>
                                <th style={{ width: 90, textAlign: 'right' }}>Cost</th>
                                <th style={{ width: 90, textAlign: 'right' }}>Core</th>
                                <th style={{ width: 80, textAlign: 'center' }}>Invoice</th>
                            </tr>
                        </thead>
                        <tbody>
                            {order.items.map((item, ii) => (
                                <tr key={ii}>
                                    <td><strong>{item.partNumber}</strong></td>
                                    <td style={{ color: '#444' }}>{item.description || '—'}</td>
                                    <td style={{ textAlign: 'center' }}>{item.quantity}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        {item.cost > 0 ? `$${item.cost.toFixed(2)}` : <span className="text-muted">—</span>}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        {item.coreCharge > 0 ? `$${item.coreCharge.toFixed(2)}` : '—'}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <Badge variant="success">✓ Added</Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ))}

            {summary.length > 1 && (
                <div style={{ textAlign: 'right', fontWeight: 700, fontSize: '1.05em', color: '#333', marginBottom: 8 }}>
                    Grand Total: ${grandTotal.toFixed(2)}
                </div>
            )}

            <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Button variant="success" onClick={onClose}>Close & Return to Invoice</Button>
            </div>
        </div>
    );
}

// ── Per-order cart section ────────────────────────────────────────────────────
function OrderSection({ order, placing, onRemove, onQtyChange, onPlaceOrder }) {
    const built        = order.builtOrder || {};
    const priceTotal   = built.priceTotal || 0;
    const coreTotal    = built.coreChargeTotal || 0;
    const shipping     = built.selectedFrightTermService;
    const freightTerms = built.availableFreightTerms || [];
    const builtErrors  = built.errors || [];

    return (
        <div style={{ marginBottom: 24, border: '1px solid #dee2e6', borderRadius: 6, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ background: '#343a40', color: '#fff', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <strong>{order.supplier?.name}</strong>
                    <span style={{ marginLeft: 12, fontSize: '0.85em', opacity: 0.8 }}>{order.store?.name}</span>
                    {shipping && (
                        <Badge variant="info" style={{ marginLeft: 10, fontSize: '0.75em' }}>
                            {shipping.method}{shipping.charge > 0 ? ` (+$${shipping.charge.toFixed(2)})` : ' (free)'}
                        </Badge>
                    )}
                </div>
                <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: '0.85em', opacity: 0.8, marginRight: 12 }}>
                        Total: ${priceTotal.toFixed(2)}{coreTotal > 0 ? ` + $${coreTotal.toFixed(2)} core` : ''}
                    </span>
                    <Button size="sm" variant="success" disabled={placing || builtErrors.length > 0} onClick={() => onPlaceOrder(order)}>
                        {placing ? <Spinner animation="border" size="sm" /> : 'Place Order'}
                    </Button>
                </div>
            </div>

            {/* Shipping options */}
            {freightTerms.length > 1 && (
                <div style={{ padding: '6px 16px', background: '#f8f9fa', borderBottom: '1px solid #dee2e6', fontSize: '0.8em', color: '#555' }}>
                    <strong>Delivery options: </strong>
                    {freightTerms.map((t, i) => (
                        <span key={i} style={{ marginRight: 16 }}>
                            {t.frightTermService?.method}
                            {t.frightTermService?.charge > 0 ? ` ($${t.frightTermService.charge.toFixed(2)})` : ' (free)'}
                        </span>
                    ))}
                </div>
            )}

            {builtErrors.length > 0 && (
                <Alert variant="warning" style={{ margin: '8px 12px 0', padding: '6px 12px', fontSize: '0.85em' }}>
                    {builtErrors.map((e, i) => <div key={i}>{e.message}</div>)}
                </Alert>
            )}

            {/* Line items */}
            <Table size="sm" style={{ margin: 0 }}>
                <thead>
                    <tr style={{ background: '#f0f4ff' }}>
                        <th>Part #</th>
                        <th>Description</th>
                        <th style={{ width: 80 }}>Qty</th>
                        <th style={{ width: 90 }}>Cost</th>
                        <th style={{ width: 90 }}>Core</th>
                        <th style={{ width: 200 }}>Store Stock</th>
                        <th style={{ width: 50 }}></th>
                    </tr>
                </thead>
                <tbody>
                    {order.items.map(item => {
                        const prod      = item.builtItem?.product;
                        const itemErrors = item.builtItem?.errors || [];
                        const allLines  = prod?.availabilityLines || [];
                        const cost      = prod?.customerPrice ?? prod?.price ?? null;

                        return (
                            <tr key={item.id} style={itemErrors.length > 0 ? { background: '#fff8f8' } : {}}>
                                <td><strong>{item.partNumber}</strong></td>
                                <td>
                                    <div>{prod?.title || item.partName || '—'}</div>
                                    {itemErrors.map((e, i) => <small key={i} className="text-danger">{e.message}</small>)}
                                </td>
                                <td>
                                    <Form.Control type="number" size="sm" min={1} value={item.quantity}
                                        style={{ width: 65 }}
                                        onChange={e => onQtyChange(item.id, parseInt(e.target.value))} />
                                </td>
                                <td>{cost != null ? `$${cost.toFixed(2)}` : <span className="text-muted">—</span>}</td>
                                <td>{prod?.coreCharge > 0 ? `$${prod.coreCharge.toFixed(2)}` : '—'}</td>
                                <td>
                                    <PartsTechStoreStock
                                        availabilityLines={allLines}
                                        homeName={order.store?.name}
                                        stopPropagation={false}
                                    />
                                </td>
                                <td>
                                    <Button size="sm" variant="outline-danger" onClick={() => onRemove(item.id)} title="Remove">×</Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
}
